import { ColumnDef } from '@tanstack/react-table';
import { ActiveSourcingChannelDetails } from 'api-clients/monolith';
import { isPast, min } from 'date-fns';
import { Info } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReturnBackArrowIcon } from '@/components/icons/return-back-arrow';
import { ChannelStatusPill } from '@/components/base/channel-status-pill';
import { ApplicantStats } from '@/components/ui/applicant-stats';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { ConversionStats } from '@/components/ui/conversion-stats';
import { CurrencyStats } from '@/components/ui/currency-stats';
import { Duration } from '@/components/ui/duration';
import { Typography } from '@/components/ui/typography';
import { useHiringGoalIdParam } from '@/hooks/use-hiring-goal-id-param';
import { useLocalDateRanges } from '@/hooks/use-local-date-ranges';
import { BOOST, OTHER, REACH, ZIPRECRUITER } from '@/types/channels';
import { getCampaignToggleState } from '@/utils/campaign-toggle-state';
import { isFreeChannel } from '@/utils/channel-util';
import { formatShortDate, toDate } from '@/utils/date-util';

import { ToggleCampaign } from '../toggle-campaign';
import { CampaignMenu } from './campaign-menu';
import { Timestamp } from './timestamp';
import { formatDayAt, getCampaignWindowAt } from './time-zone-window';
import { localDayAt } from '@/utils/time-zone';
import { SetReachPublisherStatus } from './use-reach-channels';
import {
  useOpeningContext,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { useMetricsFilterParams } from '@/hooks/use-metrics-filter-params';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/utils/classnames';
import { isNonIntegratedChannel } from '@/utils/channel-util';
interface ChannelColumnsProps {
  brand?: string;
  location?: string;
  funnel?: string;
  setReachPublisherStatus?: SetReachPublisherStatus;
}

export function useChannelColumns({
  brand,
  location,
  funnel,
  setReachPublisherStatus,
}: ChannelColumnsProps): ColumnDef<ActiveSourcingChannelDetails>[] {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const navigate = useNavigate();
  const { withHiringGoalId } = useHiringGoalIdParam();
  const [searchParams] = useSearchParams();
  const { hiringGoal, opening } = useOpeningDetails();
  // Behind the flag the card reads its window at the opening, not in the browser.
  const timeZone = useLocalDateRanges() ? opening.timeZone : undefined;
  const { metricsViewMode } = useOpeningContext();
  const {
    startDate: filterStartDate,
    endDate: filterEndDate,
    filterRange,
  } = useMetricsFilterParams();
  const isDateRangeSelected =
    metricsViewMode === 'date_range' && filterStartDate && filterEndDate;
  const handleCampaignEdit = (rowItem: ActiveSourcingChannelDetails) => {
    const {
      sourcing_purchase_id: sourcingPurchaseId,
      sourcing_channel: { slug },
    } = rowItem;

    if (!sourcingPurchaseId || !slug) return undefined;

    let channelSlug = slug;
    // Reach campaigns will ideally have the publisher slug
    if (rowItem.reach_campaign_id) {
      channelSlug = REACH;
    }

    return () => {
      navigate({
        pathname: `${channelSlug}/${sourcingPurchaseId}/edit`,
        search: withHiringGoalId(searchParams),
      });
    };
  };

  // The campaign's run and its overlap, each read at the opening.
  const getLocalDurationTooltipContent = (
    rowItem: ActiveSourcingChannelDetails,
    zone: string,
  ) => {
    const campaignWindow = getCampaignWindowAt(
      rowItem.starts_at,
      rowItem.end_at,
      zone,
    );
    if (!campaignWindow) return undefined;

    const { end } = campaignWindow;
    const runContent = end
      ? intl.formatMessage(
          {
            defaultMessage: 'Campaign duration: {start} – {end}',
            description:
              "Campaign duration at the opening. {start} and {end} are a date and time, such as Aug 30, 2026, 9:04 AM, with the zone's abbreviation",
          },
          {
            start:
              campaignWindow.startAbbr === end.abbr
                ? campaignWindow.start
                : `${campaignWindow.start} ${campaignWindow.startAbbr}`,
            end: `${end.dateTime} ${end.abbr}`,
          },
        )
      : intl.formatMessage(
          {
            defaultMessage:
              'Campaign duration: Running since {startDate}. No end date set.',
            description:
              'Campaign duration: Running since start date. No end date set.',
          },
          { startDate: `${campaignWindow.start} ${campaignWindow.startAbbr}` },
        );

    const overlapStart = rowItem.overlap_start_date ?? rowItem.starts_at;
    const overlapEnd =
      (rowItem.overlap_end_date || null) ??
      (rowItem.end_at || null) ??
      new Date();
    const overlapValues = {
      days: rowItem.duration_stats.expected,
      startDate: formatDayAt(overlapStart, zone),
      endDate: formatDayAt(overlapEnd, zone),
    };
    const overlapContent =
      metricsViewMode === 'date_range'
        ? !filterRange &&
          intl.formatMessage(
            {
              defaultMessage:
                'Overlapping duration of campaign within selected date range: {days} days ({startDate} - {endDate})',
              description:
                'Overlap duration of the campaign with the selected date range',
            },
            overlapValues,
          )
        : intl.formatMessage(
            {
              defaultMessage:
                'Overlapping duration of campaign within hiring goal: {days} days ({startDate} - {endDate})',
              description:
                'Overlap duration of the campaign with the hiring goal',
            },
            overlapValues,
          );

    if (!(rowItem.overlap_end_date || rowItem.end_at) || !overlapContent) {
      return runContent;
    }
    return (
      <>
        {runContent}
        <br />
        <br />
        {overlapContent}
      </>
    );
  };

  const getDurationTooltipContent = (rowItem: ActiveSourcingChannelDetails) => {
    if (rowItem.sourcing_channel.slug === OTHER) {
      return undefined;
    }
    if (timeZone) return getLocalDurationTooltipContent(rowItem, timeZone);
    const baseContent = !!rowItem.end_at
      ? intl.formatMessage(
          {
            defaultMessage: 'Campaign duration: {startDate} - {endDate}',
            description: 'Actual duration of the campaign',
          },
          {
            startDate: formatShortDate(rowItem.starts_at),
            endDate: formatShortDate((rowItem.end_at || null) ?? new Date()),
          },
        )
      : intl.formatMessage(
          {
            defaultMessage:
              'Campaign duration: Running since {startDate}. No end date set.',
            description:
              'Campaign duration: Running since start date. No end date set.',
          },
          {
            startDate: formatShortDate(rowItem.starts_at),
          },
        );

    const overlapStartDate = rowItem.overlap_start_date ?? rowItem.starts_at;
    const overlapEndDate =
      (rowItem.overlap_end_date || null) ??
      (rowItem.end_at || null) ??
      new Date();
    const overlapDays = rowItem.duration_stats.expected;

    const overlapContent =
      metricsViewMode === 'date_range'
        ? !filterRange &&
          intl.formatMessage(
            {
              defaultMessage:
                'Overlapping duration of campaign within selected date range: {days} days ({startDate} - {endDate})',
              description:
                'Overlap duration of the campaign with the selected date range',
            },
            {
              days: overlapDays,
              startDate: formatShortDate(overlapStartDate),
              endDate: formatShortDate(overlapEndDate ?? new Date()),
            },
          )
        : intl.formatMessage(
            {
              defaultMessage:
                'Overlapping duration of campaign within hiring goal: {days} days ({startDate} - {endDate})',
              description:
                'Overlap duration of the campaign with the hiring goal',
            },
            {
              days: overlapDays,
              startDate: formatShortDate(overlapStartDate),
              endDate: formatShortDate(overlapEndDate ?? new Date()),
            },
          );

    // Reach open-ended rows may omit end_at; overlap_* still carries the clipped window.
    if (
      (rowItem.overlap_start_date || rowItem.starts_at) &&
      (rowItem.overlap_end_date || rowItem.end_at)
    ) {
      return (
        <>
          {baseContent}
          {!!overlapContent && (
            <>
              <br />
              <br />
              {overlapContent}
            </>
          )}
        </>
      );
    }

    return baseContent;
  };

  const showOnlyActual = !hiringGoal?.external_id && !!isDateRangeSelected;

  return [
    {
      id: 'actions',
      cell: ({ row }) => {
        const {
          status,
          sourcing_purchase_id: sourcingPurchaseId,
          sourcing_channel: channel,
          reach_campaign_id: reachCampaignId,
          is_readonly: isReadonly,
          readonly_reason: readonlyReason,
        } = row.original;

        const isOtherChannel = !sourcingPurchaseId;

        const { showToggle, isToggleEnabled, tooltip } = getCampaignToggleState(
          {
            status,
            isReadonly,
            readonlyReason,
            channelSlug: channel.slug,
            isPublishable: channel.is_publishable,
            isVonqChannel: channel.is_vonq_channel,
            isOtherChannel,
          },
        );

        const showMenu = !isOtherChannel;

        if (row.original.reach_campaign_id)
          return <ReturnBackArrowIcon className="text-fx-neutral-400 w-9" />;

        return (
          <div
            className={cn(
              'flex h-full flex-col items-center gap-6',
              showToggle && showMenu ? 'justify-around' : 'justify-start',
            )}
          >
            {showToggle && (
              <Tooltip content={tooltip && intl.formatMessage(tooltip)}>
                <span className="h-4">
                  <ToggleCampaign
                    accessibleContext={{
                      openingName: opening.title,
                      channelName: channel.name,
                    }}
                    sourcingPurchaseId={sourcingPurchaseId ?? ''}
                    reachCampaignId={reachCampaignId ?? null}
                    status={status}
                    channel={channel.slug}
                    setReachPublisherStatus={setReachPublisherStatus}
                    isDisabled={!isToggleEnabled}
                  />
                </span>
              </Tooltip>
            )}
            {showMenu && (
              <CampaignMenu
                sourcingPurchaseId={sourcingPurchaseId ?? ''}
                reachCampaignId={reachCampaignId ?? null}
                status={status}
                channelSlug={channel.slug}
                onEdit={handleCampaignEdit(row.original)}
                setReachPublisherStatus={setReachPublisherStatus}
                isDisabled={isOtherChannel}
                showPauseResume={!!isToggleEnabled}
              />
            )}
          </div>
        );
      },
    },
    {
      id: 'sourcing_channel',
      accessorKey: 'sourcing_channel',
      cell: ({ row }) => {
        const { slug: channelSlug, ...channel } = row.original.sourcing_channel;

        if (row.original.reach_campaign_id)
          return (
            <ChannelIcon
              channelSlug={channelSlug}
              variant="large"
              size="md"
              channelName={channel.name}
            />
          );

        const isNonIntegrated = isNonIntegratedChannel(
          channel.is_integratable,
          channel.is_publishable,
          !!channel.is_vonq_channel,
        );

        return (
          <>
            <div className="flex w-full min-w-0 items-center gap-2">
              {/* No logo for Others */}
              <div className="flex w-full flex-col gap-1">
                <div className="flex items-center gap-3">
                  <ChannelIcon
                    channelSlug={channelSlug}
                    logoSquareUrl={channel.logo_square}
                    size="xl"
                    channelName={channel.name}
                  />
                  <ChannelStatusPill
                    status={row.original.status}
                    isNonIntegrated={isNonIntegrated}
                  />
                </div>
                <Typography variant="body2" className="text-fx-neutral-600">
                  {row.original.campaign_title ?? channel.name}
                </Typography>
              </div>
            </div>
          </>
        );
      },
    },
    {
      accessorKey: 'budget_stats',
      // Needs room for "$–1,000 / $1,000 🔒" + optional timestamp
      size: 160,
      cell: ({ row }) => {
        const channelSlug = row.original.sourcing_channel.slug;
        const isFree = isFreeChannel(
          row.original.sourcing_channel,
          row.original.budget_stats.expected == null,
          row.original.contract_id == null,
        );
        const endAt = row.original.end_at || null;
        const overlapEndDate = row.original.overlap_end_date || null;
        const expectedTooltipContent =
          endAt &&
          !isDateRangeSelected &&
          intl.formatMessage(
            {
              defaultMessage:
                'Budget available during the selected date range: {budget}',
              description:
                'Stat tooltip: derived from available budget during the selected date range',
            },
            {
              budget: formatCurrency(row.original.budget_stats.expected ?? 0),
            },
          );

        const actualTooltipContent = intl.formatMessage(
          {
            defaultMessage:
              'Budget spent within the selected date range ({startDate} - {endDate}): {budget}',
            description:
              'Stat tooltip: derived from spent budget within the selected date range',
          },
          {
            budget: formatCurrency(row.original.budget_stats.actual ?? 0),
            startDate: formatShortDate(
              row.original.overlap_start_date ?? row.original.starts_at,
            ),
            endDate: formatShortDate(
              min(
                [toDate(overlapEndDate), toDate(endAt), new Date()].filter(
                  (d): d is Date => d != null,
                ),
              ),
            ),
          },
        );

        return (
          <>
            {/* Title */}
            <Typography variant="caption">
              {intl.formatMessage({
                defaultMessage: 'Spend / Budget',
                description:
                  'Channels table: column caption for money spent on this channel',
              })}
            </Typography>

            {/* Content - Free / Managed outside Fountain / CurrencyStats */}
            {isFree ? (
              <Typography
                variant="small1"
                className="text-fx-neutral-600 italic"
              >
                <FormattedMessage
                  defaultMessage="Free"
                  description="Free of cost"
                />
              </Typography>
            ) : row.original.contract_id != null ? (
              <Typography
                variant="small1"
                className="text-fx-neutral-600 text-center italic"
              >
                <FormattedMessage
                  defaultMessage="Managed outside{br}Fountain"
                  description="Label for channels that are managed outside Fountain"
                  values={{ br: <br /> }}
                />
              </Typography>
            ) : (
              <CurrencyStats
                stats={row.original.budget_stats}
                onClick={
                  !row.original.is_readonly &&
                  row.original.status !== 'completed'
                    ? handleCampaignEdit(row.original)
                    : undefined
                }
                typographyVariant="large2"
                isApproximate={!row.original.sourcing_channel.is_integratable}
                showLockIcon={
                  channelSlug === ZIPRECRUITER || channelSlug === BOOST
                }
                showOnlyActual={showOnlyActual}
                actualBudgetTooltipContent={
                  expectedTooltipContent && actualTooltipContent ? (
                    <>
                      {actualTooltipContent}
                      <br />
                      <br />
                      {expectedTooltipContent}
                    </>
                  ) : (
                    <>{actualTooltipContent}</>
                  )
                }
              />
            )}

            {/* Last updated at timestamp */}
            {row.original.budget_stats.last_updated_at &&
              row.original.sourcing_channel.is_integratable && (
                <Timestamp
                  lastUpdatedAt={row.original.budget_stats.last_updated_at}
                />
              )}
          </>
        );
      },
    },
    {
      accessorKey: 'duration',
      // Needs room for date ranges like "Apr 17 - Jun 16" or the scheduled span text
      size: 164,
      cell: ({ row }) => {
        const isReachCompleted = Boolean(
          row.original.reach_campaign_id &&
          row.original.end_at &&
          isPast(row.original.end_at),
        );
        const isCompleted =
          row.original.status === 'completed' || isReachCompleted;
        const tooltipContent = getDurationTooltipContent(row.original);
        // Behind the flag the completed range names the opening's calendar days.
        const overlapStartAt =
          row.original.overlap_start_date ?? row.original.starts_at;
        const overlapEndAt =
          row.original.overlap_end_date ?? row.original.end_at;
        const rangeStart =
          (timeZone && localDayAt(new Date(overlapStartAt), timeZone)) ||
          overlapStartAt;
        const rangeEnd =
          (timeZone &&
            overlapEndAt &&
            localDayAt(new Date(overlapEndAt), timeZone)) ||
          overlapEndAt;
        const campaignWindow =
          timeZone && row.original.sourcing_channel.slug !== OTHER
            ? getCampaignWindowAt(
                row.original.starts_at,
                row.original.end_at,
                timeZone,
              )
            : null;
        return (
          <>
            <span className="inline-flex items-center gap-1">
              <Typography variant="caption">
                {intl.formatMessage({
                  defaultMessage: 'Duration (Days)',
                  description:
                    'Channels table: label for how many days the campaign runs',
                })}
              </Typography>
              {timeZone && tooltipContent && (
                <Tooltip content={tooltipContent} className="w-90">
                  <button
                    type="button"
                    className="cursor-help rounded-full"
                    aria-label={intl.formatMessage({
                      defaultMessage: 'Campaign duration details',
                      description:
                        'Accessible name of the info icon beside Duration (Days) on a campaign card',
                    })}
                    data-testid="campaign-duration-info"
                  >
                    <Info className="text-fx-neutral-500 size-3" />
                  </button>
                </Tooltip>
              )}
            </span>
            <Duration
              stats={row.original.duration_stats}
              brand={brand}
              location={location}
              funnel={funnel}
              typographyVariant="large2"
              showDays={false}
              showDateRange={isCompleted}
              startDate={rangeStart}
              endDate={rangeEnd}
              tooltipContent={timeZone ? undefined : tooltipContent}
              onAddButtonClick={handleCampaignEdit(row.original)}
              channelSlug={row.original.sourcing_channel.slug}
              showOnlyActual={
                showOnlyActual ||
                !!row.original.sourcing_channel.is_organic_channel
              }
            />
            {campaignWindow?.end && (
              <span
                className="flex flex-col items-center"
                data-testid="campaign-end-at"
              >
                <Typography variant="small1" className="text-fx-neutral-500">
                  {isCompleted ? (
                    <FormattedMessage
                      defaultMessage="Ended {date}"
                      description="Campaign card: the day a completed campaign ended at the opening, such as Aug 27"
                      values={{ date: campaignWindow.end.monthDay }}
                    />
                  ) : (
                    <FormattedMessage
                      defaultMessage="Ends {date}"
                      description="Campaign card: the day the campaign ends at the opening, such as Sep 24"
                      values={{ date: campaignWindow.end.monthDay }}
                    />
                  )}
                </Typography>
                <Typography variant="small2" className="text-fx-neutral-600">
                  {`${campaignWindow.end.time} ${campaignWindow.end.abbr}`}
                </Typography>
              </span>
            )}
          </>
        );
      },
    },
    {
      accessorKey: 'applicants_stats',
      size: 90,
      cell: ({ row }) => (
        <>
          <Typography variant="caption">
            {intl.formatMessage({
              defaultMessage: 'Applicants',
              description:
                'Channels table: column caption for number of applicants',
            })}
          </Typography>
          <ApplicantStats
            stats={row.original.applicants_stats}
            typographyVariant="large2"
            showOnlyActual
          />
        </>
      ),
    },
    {
      accessorKey: 'cpa_stats',
      size: 70,
      cell: ({ row }) => {
        const isFree = isFreeChannel(
          row.original.sourcing_channel,
          row.original.budget_stats.expected == null,
          row.original.contract_id == null,
        );

        return (
          <>
            <Typography variant="caption">
              {intl.formatMessage({
                defaultMessage: 'CPA',
                description:
                  'Channels table: cost-per-applicant acronym (not spelled out for layout)',
              })}
            </Typography>
            {isFree ? (
              <Typography
                variant="small1"
                className="text-fx-neutral-600 italic"
              >
                <FormattedMessage
                  defaultMessage="Free"
                  description="Displayed when a Pool channel has no cost for CPA (no spend)"
                />
              </Typography>
            ) : (
              <CurrencyStats
                stats={row.original.cpa_stats}
                onClick={
                  row.original.status !== 'completed' &&
                  row.original.contract_id == null
                    ? handleCampaignEdit(row.original)
                    : undefined
                }
                typographyVariant="large2"
                separator={<Separator />}
                tooltipContent={intl.formatMessage({
                  defaultMessage:
                    'The estimated cost per applicant for this channel based on the budget and target number of applicants',
                  description:
                    'Stat tooltip: derived CPA from budget and applicant target',
                })}
                showOnlyActual
              />
            )}
          </>
        );
      },
    },
    {
      accessorKey: 'conversion_stats',
      size: 95,
      cell: ({ row }) => {
        const isFree = isFreeChannel(
          row.original.sourcing_channel,
          row.original.budget_stats.expected == null,
          row.original.contract_id == null,
        );
        return (
          <>
            <Typography variant="caption">
              {intl.formatMessage({
                defaultMessage: 'Conversion',
                description:
                  'Channels table: column caption for applicant-to-hire conversion',
              })}
            </Typography>
            <ConversionStats
              stats={row.original.conversion_stats}
              onClick={
                row.original.status !== 'completed' &&
                !isFree &&
                row.original.contract_id == null
                  ? handleCampaignEdit(row.original)
                  : undefined
              }
              typographyVariant="large2"
              separator={<Separator />}
              tooltipContent={intl.formatMessage({
                defaultMessage:
                  'The predicted conversion rate of applicants to hires based on target numbers',
                description:
                  'Stat tooltip: forecast conversion from targets, not only actuals',
              })}
              showOnlyActual
            />
          </>
        );
      },
    },
    {
      accessorKey: 'hire_stats',
      size: 70,
      cell: ({ row }) => (
        <>
          <Typography variant="caption">
            {intl.formatMessage({
              defaultMessage: 'Hires',
              description:
                'Channels table: column caption for number of hires attributed',
            })}
          </Typography>
          <ApplicantStats
            stats={row.original.hire_stats}
            typographyVariant="large2"
            showOnlyActual
          />
        </>
      ),
    },
  ];
}

function Separator() {
  return <span className="bg-fx-neutral-300 mx-1 h-6 w-px" />;
}
